import React from 'react'

import { type MonthlyDistribution } from './ImportDataContext'
import { DIST_COLORS } from './importFormStyles'

interface BellCurveChartProps {
  revenue: MonthlyDistribution
  expenses: MonthlyDistribution
  bills: MonthlyDistribution
}

const pdf = (x: number, mean: number, stdDev: number) =>
  stdDev > 0 ? Math.exp(-0.5 * ((x - mean) / stdDev) ** 2) : 0

export const BellCurveChart = ({ revenue, expenses, bills }: BellCurveChartProps) => {
  const W = 560
  const H = 150
  const PAD = { top: 16, right: 16, bottom: 28, left: 16 }
  const cW = W - PAD.left - PAD.right
  const cH = H - PAD.top - PAD.bottom

  const xMax = Math.max(revenue.max, expenses.max, bills.max) * 1.1 || 30000
  const toXPx = (x: number) => PAD.left + (x / xMax) * cW

  const makePoly = (dist: MonthlyDistribution, color: string) => {
    const STEPS = 200
    const top: string[] = []
    for (let i = 0; i <= STEPS; i++) {
      const x = (i / STEPS) * xMax
      top.push(`${toXPx(x).toFixed(1)},${(PAD.top + cH - pdf(x, dist.mean, dist.stdDev) * cH * 0.85).toFixed(1)}`)
    }
    const base = `${toXPx(xMax).toFixed(1)},${(PAD.top + cH).toFixed(1)} ${toXPx(0).toFixed(1)},${(PAD.top + cH).toFixed(1)}`
    return <polygon points={[...top, base].join(' ')} fill={color} fillOpacity={0.15} stroke={color} strokeWidth={2} strokeOpacity={0.85} />
  }

  const ticks = Array.from({ length: 6 }, (_, i) => (i / 5) * xMax)
  const dists = [
    { dist: bills, color: DIST_COLORS.bills },
    { dist: expenses, color: DIST_COLORS.expenses },
    { dist: revenue, color: DIST_COLORS.revenue },
  ]

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
      {dists.map(({ dist, color }, i) => <React.Fragment key={i}>{makePoly(dist, color)}</React.Fragment>)}
      {dists.map(({ dist, color }, i) => (
        <line key={i} x1={toXPx(dist.mean)} y1={PAD.top} x2={toXPx(dist.mean)} y2={PAD.top + cH}
          stroke={color} strokeWidth={1.5} strokeDasharray='4,3' opacity={0.6} />
      ))}
      <line x1={PAD.left} y1={PAD.top + cH} x2={PAD.left + cW} y2={PAD.top + cH} stroke='#d1d5db' />
      {ticks.map((val, i) => (
        <g key={i}>
          <line x1={toXPx(val)} y1={PAD.top + cH} x2={toXPx(val)} y2={PAD.top + cH + 4} stroke='#d1d5db' />
          <text x={toXPx(val)} y={H - 4} textAnchor='middle' fontSize={10} fill='#9ca3af'>
            {val >= 1000 ? `$${(val / 1000).toFixed(0)}k` : `$${Math.round(val)}`}
          </text>
        </g>
      ))}
      {(['Revenue', 'Expenses', 'Bills'] as const).map((label, i) => {
        const color = [DIST_COLORS.revenue, DIST_COLORS.expenses, DIST_COLORS.bills][i]
        return (
          <g key={label} transform={`translate(${PAD.left + i * 95}, 2)`}>
            <rect width={10} height={10} rx={2} fill={color} opacity={0.75} y={2} />
            <text x={14} y={11} fontSize={11} fill='#374151'>{label}</text>
          </g>
        )
      })}
    </svg>
  )
}
